/**
 * «Записи» (`/appointments`): сколько уникальных людей записали на примерку (вход сделки в «ждем
 * клиента»), сколько из них пришли и забронировали, и почему не пришли остальные. По умолчанию —
 * рейтинг сотрудников за 7 дней (`AppointmentsStaff.tsx`); `?group=week` — по неделям с
 * понедельника, `?group=day` — по дням, человек — склейка номеров и сделок. Выбор периода —
 * второй строкой, слева под заголовком; справа сверху — «Инструкция» (`?help=1`,
 * текст `web/src/help/appointments.md`). Клик по числу — список людей ниже. Спека `docs/specs/2026-09-30-appointments-design.md`; числа — те же, что у
 * `prokat-runner appointments-report`.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker, type Окно, прошлыеОкна } from "@/components/DateRangePicker";
import { HelpButton, HelpPanel } from "@/components/HelpPanel";
import { LeadLink } from "@/components/LeadLink";
import { DurationView, TYPE_CHOICES } from "@/pages/AppointmentsDuration";
import { FunnelCompare, FunnelView } from "@/pages/AppointmentsFunnel";
import help from "@/help/appointments.md?raw";
import { iso, plusDays } from "@/lib/dates";
import {
  type AppointmentsReport as Data,
  type Group,
  loadAppointmentPeople,
  loadAppointments,
  loadAppointmentsStaff,
  type StaffMethod,
  type Status,
} from "@/lib/appointments";
import { type StaffPick, type StaffSort, type StaffStatus, StaffView, staffWindows } from "@/pages/AppointmentsStaff";
import { card, Num, pct, td, th, Tile } from "@/pages/AppointmentsUi";

const STATUS: Record<Status, string> = { booked: "забронировали", came: "пришли без брони", no_show: "не пришли" };

type Pick = { from: string; to: string; status?: Status | undefined; reason?: string | undefined; title: string };

function addDays(iso: string, n: number) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString("sv-SE");
}

/** Окна недельного вида — целыми неделями с понедельника: сервер всё равно ровняет начало на понедельник. */
function неделиОкна(): Окно[] {
  const today = new Date();
  const t = iso(today);
  const пн = plusDays(today, -((today.getDay() + 6) % 7));
  return [
    { label: "эта неделя", hint: "с понедельника по сегодня", from: iso(пн), to: t },
    { label: "4 недели", hint: "эта и 3 прошлые недели", from: iso(plusDays(пн, -21)), to: t },
    { label: "12 недель", hint: "эта и 11 прошлых недель", from: iso(plusDays(пн, -77)), to: t },
    { label: "26 недель", hint: "эта и 25 прошлых недель", from: iso(plusDays(пн, -175)), to: t },
  ];
}

function Periods({ d, pick, setPick }: { d: Data; pick: Pick | null; setPick: (p: Pick) => void }) {
  const is = (p: Pick) => pick?.from === p.from && pick?.status === p.status && pick?.reason === p.reason;
  return (
    <section className={card} data-annot="записи по периодам">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">{d.group === "week" ? "По неделям" : "По дням"}</h2>
        <span className="text-[12px] text-fg-faint">
          день записи; человек в периоде — один раз; пришли и бронь — в {d.window_days} дн. от первой записи периода;
          бронь — % от пришедших
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-1.5 text-left font-medium">{d.group === "week" ? "Неделя" : "День"}</th>
              <th className={th}>Записали</th>
              <th className={th}>Пришли</th>
              <th className={th}>%</th>
              <th className={th}>Забронировали</th>
              <th className={th}>% от пришедших</th>
              <th className={th}>Не пришли</th>
              <th className={th} title="не пришли — от записанных">
                %
              </th>
              <th className={th}>из них ещё ждём</th>
            </tr>
          </thead>
          <tbody>
            {d.rows.map((r) => {
              const to = d.group === "week" ? addDays(r.key, 6) : r.key;
              const p = (status?: Status, reason?: string): Pick => ({
                from: r.key,
                to,
                status,
                reason,
                title: `${r.label}${status ? ` · ${STATUS[status]}` : ""}`,
              });
              return (
                <tr key={r.key} className="border-t border-border hover:bg-bg-hover" data-annot={`записи ${r.key}`}>
                  <td className="whitespace-nowrap px-4 py-1.5">{r.label}</td>
                  <td className={`${td} font-medium`}>
                    <Num n={r.recorded} active={is(p())} on={() => setPick(p())} />
                  </td>
                  <td className={td}>{r.came}</td>
                  <td className={`${td} text-fg-muted`}>{pct(r.came, r.recorded)}</td>
                  <td className={td}>
                    <Num n={r.booked} active={is(p("booked"))} on={() => setPick(p("booked"))} />
                  </td>
                  <td className={`${td} text-fg-muted`}>{pct(r.booked, r.came)}</td>
                  <td className={td}>
                    <Num n={r.no_show} active={is(p("no_show"))} on={() => setPick(p("no_show"))} />
                  </td>
                  <td className={`${td} text-fg-muted`}>{pct(r.no_show, r.recorded)}</td>
                  <td className={`${td} text-fg-muted`}>{r.waiting || "·"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Reasons({ d, pick, setPick }: { d: Data; pick: Pick | null; setPick: (p: Pick) => void }) {
  const total = d.total.no_show;
  return (
    <section className={card} data-annot="причины не пришли">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Почему не пришли</h2>
        <span className="text-[12px] text-fg-faint">стадия последней записанной сделки сейчас · весь отрезок</span>
      </div>
      <table className="w-full text-[12.5px]">
        <tbody>
          {d.reasons.map((r) => (
            <tr key={r.code} className="border-t border-border first:border-t-0 hover:bg-bg-hover">
              <td className="px-4 py-1.5">{r.label}</td>
              <td className={td}>
                <Num
                  n={r.count}
                  active={pick?.reason === r.code && pick.from === d.from}
                  on={() =>
                    setPick({ from: d.from, to: d.to, status: "no_show", reason: r.code, title: `не пришли · ${r.label}` })
                  }
                />
              </td>
              <td className={`${td} w-16 text-fg-muted`}>{pct(r.count, total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function People({ pick }: { pick: Pick }) {
  const q = useQuery({
    queryKey: ["appointment-people", pick.from, pick.to, pick.status, pick.reason],
    queryFn: () => loadAppointmentPeople(pick),
  });
  return (
    <section className={card} data-annot="люди записи">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">{pick.title}</h2>
        {q.data && <span className="text-[12px] text-fg-faint">{q.data.total} чел.</span>}
      </div>
      {q.isPending && <p className="px-4 py-3 text-[12.5px] text-fg-muted">Загружаю…</p>}
      {q.error && <p className="px-4 py-3 text-[12.5px] text-err">{String(q.error)}</p>}
      {q.data && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-[11.5px] text-fg-faint">
              <tr>
                <th className="px-4 py-1.5 text-left font-medium">Записали</th>
                <th className="px-3 py-1.5 text-left font-medium">Номер</th>
                <th className="px-3 py-1.5 text-left font-medium">Сделки</th>
                <th className="px-3 py-1.5 text-left font-medium">Лиды</th>
                <th className="px-3 py-1.5 text-left font-medium">Итог</th>
                <th className="px-3 py-1.5 text-left font-medium">Приход</th>
                <th className="px-3 py-1.5 text-left font-medium">Бронь</th>
              </tr>
            </thead>
            <tbody>
              {q.data.people.map((p) => (
                <tr key={`${p.deals[0]}-${p.recorded_at}`} className="border-t border-border align-top hover:bg-bg-hover">
                  <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{p.recorded_at.slice(0, 16)}</td>
                  <td className="px-3 py-1.5 font-mono">
                    {p.phones.length ? (
                      p.phones.map((ph) => (
                        <Link key={ph} to={`/search?q=${ph}`} className="mr-2 hover:underline" title="поиск по номеру">
                          {ph}
                        </Link>
                      ))
                    ) : (
                      <span className="text-fg-faint">нет номера</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 font-mono">
                    {p.deals.map((d) => (
                      <Link key={d} to={`/deals/${d}`} className="mr-2 underline" title="карточка сделки">
                        {d}
                      </Link>
                    ))}
                  </td>
                  <td className="px-3 py-1.5">
                    {p.leads.length ? (
                      p.leads.map((l) => <LeadLink key={l} id={l} className="mr-2" />)
                    ) : (
                      <span className="text-fg-faint">·</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    {p.status === "no_show" ? <span className="text-fg-muted">{p.reason_label}</span> : STATUS[p.status]}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{p.visit_at?.slice(0, 16) ?? "·"}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{p.booking_at?.slice(0, 16) ?? "·"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type View = Group | "staff" | "funnel" | "duration";
const VIEWS: { id: View; label: string }[] = [
  { id: "day", label: "по дням" },
  { id: "week", label: "по неделям" },
  { id: "staff", label: "по сотрудникам" },
  { id: "funnel", label: "воронка" },
  { id: "duration", label: "длительность" },
];
const SORTS: StaffSort[] = ["recorded", "came", "came_pct", "booked", "booked_pct", "bonus"];
const METHOD_IDS: StaffMethod[] = ["call_in", "call_lead", "call_deal", "chat", "link", "other"];
const STAFF_STATUSES: StaffStatus[] = ["came", "booked", "no_show", "waiting", "bonus"];
/** Отбор списка людей — в адресе: у периодов `p*`, у сотрудников `s*`. */
const NO_PICK = { pf: undefined, pt: undefined, ps: undefined, pr: undefined, pn: undefined, su: undefined, sm: undefined, ss: undefined };

export function AppointmentsPage() {
  const [params, setParams] = useSearchParams();
  // По умолчанию — сотрудники за 7 дней (просьба владельца 07.10.26); недели — `?group=week`, дни — `?group=day`.
  const raw = params.get("group");
  const view: View = raw === "day" || raw === "week" || raw === "funnel" || raw === "duration" ? raw : "staff";
  const from = params.get("from") ?? undefined;
  const to = params.get("to") ?? undefined;
  const окно = from || to ? null : { from: iso(plusDays(new Date(), -6)), to: iso(new Date()) };
  const week = окно && view === "staff" ? окно : null;
  // «Воронка» всегда считает по конкретному отрезку: пусто в адресе — последние 7 дней.
  const funnelFrom = from ?? окно?.from ?? iso(plusDays(new Date(), -6));
  const funnelTo = to ?? окно?.to ?? iso(new Date());
  // Второй отрезок «Воронки» — сравнение маршрутов (просьба владельца 20.10.26).
  const cfrom = params.get("cfrom") ?? "";
  const cto = params.get("cto") ?? "";
  // «Длительности» нужен объём: по умолчанию 90 дней.
  const durFrom = from ?? iso(plusDays(new Date(), -89));
  const durTo = to ?? iso(new Date());
  const durTypes = TYPE_CHOICES.find((c) => c.id === params.get("dt"))?.id ?? "1";
  const pickFrom = params.get("pf");
  const set = (next: Record<string, string | undefined>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) out.set(k, v);
      else out.delete(k);
    }
    setParams(out, { replace: true });
  };
  const q = useQuery({
    queryKey: ["appointments", view, from, to],
    queryFn: () => loadAppointments({ group: view === "week" ? "week" : "day", from, to }),
    enabled: view === "day" || view === "week",
  });
  const sq = useQuery({
    queryKey: ["appointments-staff", week?.from ?? from, week?.to ?? to],
    queryFn: () => loadAppointmentsStaff(week ?? { from, to }),
    enabled: view === "staff",
  });
  const d = view === "day" || view === "week" ? q.data : undefined;
  const s = view === "staff" ? sq.data : undefined;
  const active = view === "staff" ? sq : q;
  const pick: Pick | null = pickFrom
    ? {
        from: pickFrom,
        to: params.get("pt") ?? pickFrom,
        status: (params.get("ps") as Status) || undefined,
        reason: params.get("pr") ?? undefined,
        title: params.get("pn") ?? "",
      }
    : null;
  const setPick = (p: Pick) =>
    set({ pf: p.from, pt: p.to, ps: p.status, pr: p.reason, pn: p.title });
  const method = METHOD_IDS.find((m) => m === params.get("m"));
  const sort = SORTS.find((x) => x === params.get("sort")) ?? "recorded";
  const staffPick: StaffPick | null =
    params.has("su") || params.has("sm") || params.has("ss")
      ? {
          user: params.get("su") || undefined,
          method: METHOD_IDS.find((m) => m === params.get("sm")),
          status: STAFF_STATUSES.find((x) => x === params.get("ss")),
        }
      : null;
  // Пока новый отрезок грузится, кнопка периода остаётся на месте — с датами из адреса.
  // У «Воронки» свой отрезок по умолчанию: без этой ветки кнопка периода на ней не рисовалась.
  const shownFrom =
    view === "duration" ? durFrom : view === "funnel" ? funnelFrom : (d?.from ?? s?.from ?? from ?? week?.from);
  const shownTo = view === "duration" ? durTo : view === "funnel" ? funnelTo : (d?.to ?? s?.to ?? to ?? week?.to);
  const t = d?.total;
  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-col gap-2.5" data-annot="шапка записей">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Записи</h1>
          <div className="flex gap-1" role="radiogroup" aria-label="Вид">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                role="radio"
                aria-checked={view === v.id}
                onClick={() => {
                  // Недели ровняют начало на понедельник — свой отрезок; дни и сотрудники отрезок делят.
                  const keep = view !== "week" && v.id !== "week";
                  set({
                    ...NO_PICK,
                    dt: undefined,
                    dl: undefined,
                    dby: undefined,
                    cfrom: undefined,
                    cto: undefined,
                    group: v.id === "staff" ? undefined : v.id,
                    from: keep ? from : undefined,
                    to: keep ? to : undefined,
                  });
                }}
                className={`rounded-full border px-2.5 py-0.5 text-[12px] ${
                  view === v.id ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <HelpButton className="ml-auto" onClick={() => set({ help: "1" })} />
        </div>
        <div className="flex min-h-8 flex-wrap items-center gap-3" data-annot="выбор периода">
          {shownFrom && shownTo && (
            <DateRangePicker
              label="Период"
              from={shownFrom}
              to={shownTo}
              windows={view === "week" ? неделиОкна() : view === "day" ? прошлыеОкна() : staffWindows()}
              onChange={(a, b) => set({ ...NO_PICK, from: a, to: b })}
            />
          )}
          {view === "funnel" && (
            <FunnelCompare
              from={funnelFrom}
              to={funnelTo}
              cfrom={cfrom}
              cto={cto}
              onChange={(a, b) => set({ cfrom: a, cto: b })}
            />
          )}
        </div>
      </div>
      {/* «Воронка» и «Длительность» грузят себя сами: общий запрос у них выключен и вечно «pending». */}
      {view !== "funnel" && view !== "duration" && active.isPending && (
        <p className="text-[12.5px] text-fg-muted">Загружаю…</p>
      )}
      {active.error && <p className="text-[12.5px] text-err">{String(active.error)}</p>}
      {s && (
        <StaffView
          d={s}
          method={method}
          setMethod={(m) => set({ ...NO_PICK, m })}
          sort={sort}
          setSort={(x) => set({ sort: x === "recorded" ? undefined : x })}
          pick={staffPick}
          // «Всего записали» — отбор без условий: в адресе его держит `ss=all`.
          setPick={(p) => set({ su: p.user, sm: p.method, ss: p.status ?? (p.user || p.method ? undefined : "all") })}
        />
      )}
      {view === "funnel" && (
        <FunnelView from={funnelFrom} to={funnelTo} cfrom={cfrom && cto ? cfrom : ""} cto={cfrom && cto ? cto : ""} />
      )}
      {view === "duration" && (
        <DurationView
          from={durFrom}
          to={durTo}
          types={durTypes}
          location={params.get("dl") ?? undefined}
          by={params.get("dby") ?? "location"}
          set={set}
        />
      )}
      {d && t && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-annot="итог записей">
            <Tile
              label="Записали, уникальных людей"
              value={t.recorded}
              hint={`входов в «ждем клиента» ${d.records}; без номера ${t.without_phone}`}
            />
            <Tile label="Пришли на примерку" value={t.came} hint={`${pct(t.came, t.recorded)} от записанных`} />
            <Tile label="Забронировали" value={t.booked} hint={`${pct(t.booked, t.came)} от пришедших`} />
            <Tile
              label="Не пришли"
              value={t.no_show}
              hint={`${pct(t.no_show, t.recorded)} от записанных · из них ещё ждём ${t.waiting}`}
            />
          </div>
          <p className="text-[12px] text-fg-faint">
            Итог — уникальные люди за весь отрезок {d.from} — {d.to}, не сумма строк. Запись — вход сделки в «ждем
            клиента»; человек — склейка номеров контактов и лида сделки; приход — примерка в CRM по номеру клиента или
            привязке к сделке; бронь — договор в «Бронь»/«Подписан».
          </p>
          <div className="grid gap-4 xl:grid-cols-[1fr_22rem]">
            <Periods d={d} pick={pick} setPick={setPick} />
            <Reasons d={d} pick={pick} setPick={setPick} />
          </div>
          {pick && <People pick={pick} />}
        </>
      )}
      {params.has("help") && <HelpPanel markdown={help} onClose={() => set({ help: undefined })} />}
    </main>
  );
}
